import tenantUtils from '@utils';
import tenantData from '@data';
import { isBundleProductTitle } from '../apis/bundles';

export const cartDataMapper = (response, meta, arg) => {
  if (arg?.orderId) {
    const { id, status, order_details, total, gross_amount, tax_amount, tax_percentage } = response?.order;
    if (status === 'completed') {
      return { error: `Order is in ${status} state` };
    }

    // Bundle order lines carry none of the cart's decorations (no category/products/
    // discount_percentage) — the only bundle signal here is the product title BE gives the line.
    // No per-line tax breakdown, only order-level total (pre-tax), gross_amount (tax-inclusive),
    // tax_amount, and tax_percentage.
    const bundleOrderDetails = order_details?.filter((e) => isBundleProductTitle(e?.product?.product_title));

    return {
      available_channels: response?.order?.available_channels,
      cartProducts: tenantUtils.cartProductList(
        order_details?.map((e) => ({
          id: e.id,
          product_id: e.product?.product_id,
          product: {
            id: e.product?.product_id,
            title: e.product?.product_title,
            slug: tenantData.quotaCreditProducts?.find((pr) => pr?.id == e?.product?.product_id)?.slug,
            price: e.price,
          },
          quantity: e.quantity,
          price: e.price,
          products: e?.products,
          item_type: e?.product_type,
          source: 'profolio',
        })),
      ),
      bundleCartDetails:
        bundleOrderDetails?.map((e) => ({
          id: e.id,
          title: e.product?.product_title,
          quantity: e.quantity,
          item_price: e.price,
        })) ?? [],
      cartId: id,
      source: 'profolio',
      subtotal: total,
      taxAmount: tax_amount ?? (total != null && gross_amount != null ? gross_amount - total : undefined),
      taxPercentage: tax_percentage,
      amount: gross_amount,
      total: gross_amount ?? total,
    };
  } else if (!(response?.cart ?? response?.data?.cart)?.cart_details?.length) {
    return { cartProducts: [], bundleCartDetails: [] };
  } else {
    const cart = response?.cart ?? response?.data?.cart;
    const { id, source, cart_details } = cart;
    return {
      available_channels: cart?.available_channels,
      cartProducts: tenantUtils.cartProductList(cart_details),
      // Bundle lines carry a multi-product breakdown (`products`/`category`/`discount_percentage`)
      // that cartProductList's single-product lookup can't render — kept raw here for
      // bundle-specific UI, plus normalized title/quantity fields matching the order shape.
      // The BE response has no item_type field to key off, so bundle lines are identified by
      // the decorations only bundles carry.
      bundleCartDetails:
        cart_details
          ?.filter((e) => Array.isArray(e?.products) && e?.category)
          .map((e) => ({ ...e, title: e.item_title, quantity: e.item_quantity })) ?? [],
      cartId: id,
      source: source,
      total: cart?.net_amount ?? cart?.amount,
      subtotal: cart?.subtotal,
      taxAmount: cart?.tax_amount,
      taxPercentage: cart?.tax_percentage,
      amount: cart?.amount,
      discount: cart?.discount,
      purchasedFullCredits: cart?.purchased_full_credits,
      listing_id: cart?.listing_id,
    };
  }
};

const getActiveProductsMapper = (response) => {
  const productsByPlatform = { zameen: [], olx: [] };
  if (response) {
    if (response.error) {
      return response;
    } else if (!response?.products || response?.products?.length === 0) {
      return {
        platformProducts: productsByPlatform,
        products: [],
      };
    } else if (response?.products) {
      const productList = response?.products;
      productList &&
        productList?.length > 0 &&
        productList?.forEach((product) => {
          const { product_id, key: slug } = product;
          if (slug && slug.includes('olx')) {
            productsByPlatform.olx.push({
              ...tenantData.getListingActions(slug),
              ...product,
              product_id,
              price: tenantUtils.makePriceForPackage('olx', product),
            });
          } else {
            productsByPlatform.zameen.push({
              ...tenantData.getListingActions(slug),
              ...product,
              product_id,
              price: tenantUtils.makePriceForPackage('zameen', product),
            });
          }
        });
      return {
        platformProducts: productsByPlatform,
        products: productList.map((e) => ({ ...tenantData.getListingActions(e.key), ...e })),
      };
    }
  }
};

export default { cartDataMapper, getActiveProductsMapper };
